import {
  buildBookmarkedSection,
  buildEditsSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-special-sections';
import {
  buildDisplayInfoSection,
  buildRangeSections,
  buildUOMSection,
  buildWeeklyPurchaseQuantitiesSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-column-sections';
import { DetailColumnId } from '@sales-portal/domain/pricing/screens/customers/types';
import { PricingCustomerDetailRow } from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-detail-transformer';
import {
  PricingFilterSection,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';

interface BuildCustomerDetailFilterSectionsArgs {
  bookmarkedByVariantPack?: Record<string, boolean>;
  columnIds: string[];
  // Localized labels for the server-driven columns, keyed by header key.
  displayInfoTitles: Record<string, string>;
  editCounts: Record<string, number>;
  rangeColumnConfigs: Record<
    string,
    PricingRangeColumnConfig<PricingCustomerDetailRow>
  >;
  rows: PricingCustomerDetailRow[];
  timezone: string;
}

const getRowId = (row: PricingCustomerDetailRow): string => row.variantPackUUID;

export const buildCustomerDetailFilterSections = ({
  bookmarkedByVariantPack,
  columnIds,
  displayInfoTitles,
  editCounts,
  rangeColumnConfigs,
  rows,
  timezone,
}: BuildCustomerDetailFilterSectionsArgs): PricingFilterSection<PricingCustomerDetailRow>[] => {
  const sections: PricingFilterSection<PricingCustomerDetailRow>[] = [
    buildEditsSection(editCounts, getRowId),
  ];

  if (bookmarkedByVariantPack) {
    sections.push(buildBookmarkedSection(bookmarkedByVariantPack, getRowId));
  }

  for (const columnId of columnIds) {
    if (columnId === DetailColumnId.UOM) {
      sections.push(buildUOMSection(columnId, rows));
      continue;
    }

    if (columnId === DetailColumnId.WeeklyPurchaseQuantities) {
      sections.push(
        buildWeeklyPurchaseQuantitiesSection(columnId, rows, timezone),
      );
      continue;
    }

    const displayInfoTitle = displayInfoTitles[columnId];
    if (displayInfoTitle) {
      sections.push(buildDisplayInfoSection(columnId, displayInfoTitle, rows));
      continue;
    }

    const config = rangeColumnConfigs[columnId];
    if (!config) {
      continue;
    }

    sections.push(...buildRangeSections(columnId, config, rows));
  }

  return sections;
};
